<template>
  <div ref="rootRef" class="flex flex-wrap gap-1.5 items-start">
    <template v-for="(tag, index) in visibleTags" :key="tag.key">
      <span v-if="renderLabel" :class="resolveTagClass(tag, index)" v-html="renderLabel(tag.label)"></span>
      <span v-else :class="resolveTagClass(tag, index)">{{ tag.label }}</span>
    </template>

    <button
      v-if="hasHiddenTags && !expanded"
      ref="chipRef"
      type="button"
      class="inline-flex items-center rounded-full border border-border bg-muted px-2 py-1 text-[10px] font-semibold text-foreground hover:bg-muted/70 transition-colors"
      @click="toggleExpanded"
      @mouseenter="handleChipMouseEnter"
      @mouseleave="handleChipMouseLeave"
      @focus="handleChipMouseEnter"
      @blur="handleChipMouseLeave"
    >
      +{{ hiddenCount }} more
    </button>

    <button
      v-if="expanded"
      type="button"
      class="inline-flex items-center rounded-full border border-border bg-muted px-2 py-1 text-[10px] font-semibold text-foreground hover:bg-muted/70 transition-colors"
      @click="toggleExpanded"
    >
      Show less
    </button>

    <Teleport to="body">
      <div
        v-if="showPopover && hasHiddenTags && !expanded"
        class="fixed z-[9999] max-w-xs rounded-lg border border-border bg-card text-foreground shadow-lg p-3"
        :style="popoverStyle"
        @mouseenter="handlePopoverMouseEnter"
        @mouseleave="handlePopoverMouseLeave"
      >
        <div class="text-[10px] font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
          {{ hiddenCount }} more skill{{ hiddenCount === 1 ? '' : 's' }}
        </div>
        <div class="flex flex-wrap gap-1.5">
          <template v-for="(tag, index) in hiddenTags" :key="'popover-' + tag.key">
            <span
              v-if="renderLabel"
              :class="resolveTagClass(tag, visibleTags.length + index)"
              v-html="renderLabel(tag.label)"
            ></span>
            <span v-else :class="resolveTagClass(tag, visibleTags.length + index)">{{ tag.label }}</span>
          </template>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref } from 'vue';

const props = defineProps({
  tags: {
    type: Array,
    default: () => [],
  },
  max: {
    type: Number,
    default: 6,
  },
  // String of classes applied to every pill, or a function(tag, index) => string
  // for per-tag classes. Lets each call site keep its own existing pill styling.
  tagClass: {
    type: [String, Function],
    default: 'inline-flex items-center rounded-full border border-border bg-muted px-2 py-1 text-[11px] text-foreground',
  },
  // Optional (tag) => string of HTML. When provided, the label is rendered with
  // v-html (used by JobFiltersPage's own sanitized highlightText). When omitted,
  // labels are always rendered as plain text to avoid an unnecessary v-html/XSS surface.
  renderLabel: {
    type: Function,
    default: null,
  },
});

const rootRef = ref(null);
const chipRef = ref(null);
const expanded = ref(false);
const showPopover = ref(false);
const popoverStyle = ref({ top: '0px', left: '0px' });
let popoverHideTimer = null;

const normalizedTags = computed(() =>
  (props.tags || []).map((tag, index) => {
    const label = typeof tag === 'string' ? tag : (tag && tag.name) || '';
    const key = (tag && typeof tag === 'object' && tag.id) || label || index;
    return { raw: tag, label, key: `${key}-${index}` };
  }),
);

const hasHiddenTags = computed(() => normalizedTags.value.length > props.max);
const hiddenCount = computed(() => Math.max(normalizedTags.value.length - props.max, 0));

const visibleTags = computed(() =>
  expanded.value ? normalizedTags.value : normalizedTags.value.slice(0, props.max),
);

const hiddenTags = computed(() => normalizedTags.value.slice(props.max));

function resolveTagClass(tag, index) {
  if (typeof props.tagClass === 'function') {
    return props.tagClass(tag.raw, index);
  }
  return props.tagClass;
}

function supportsHover() {
  return typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(hover: hover) and (pointer: fine)').matches;
}

function clearPopoverHideTimer() {
  if (popoverHideTimer) {
    clearTimeout(popoverHideTimer);
    popoverHideTimer = null;
  }
}

function positionPopover() {
  if (!chipRef.value) return;
  const rect = chipRef.value.getBoundingClientRect();
  const popoverWidth = 288; // max-w-xs (~18rem)
  const popoverHeight = 160; // rough estimate, clamped below anyway
  const margin = 8;

  let left = rect.left;
  let top = rect.bottom + margin;

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  if (left + popoverWidth > viewportWidth - margin) {
    left = Math.max(margin, viewportWidth - popoverWidth - margin);
  }
  if (top + popoverHeight > viewportHeight - margin) {
    // Flip above the chip if it would overflow the bottom edge.
    const above = rect.top - margin - popoverHeight;
    top = above > margin ? above : Math.max(margin, viewportHeight - popoverHeight - margin);
  }

  popoverStyle.value = {
    top: `${Math.max(margin, top)}px`,
    left: `${Math.max(margin, left)}px`,
  };
}

async function handleChipMouseEnter() {
  if (expanded.value || !supportsHover()) return;
  clearPopoverHideTimer();
  showPopover.value = true;
  await nextTick();
  positionPopover();
}

function handleChipMouseLeave() {
  if (!supportsHover()) return;
  clearPopoverHideTimer();
  // Small delay so the user can move the pointer onto the popover itself.
  popoverHideTimer = setTimeout(() => {
    showPopover.value = false;
  }, 120);
}

function handlePopoverMouseEnter() {
  clearPopoverHideTimer();
}

function handlePopoverMouseLeave() {
  clearPopoverHideTimer();
  popoverHideTimer = setTimeout(() => {
    showPopover.value = false;
  }, 120);
}

function toggleExpanded() {
  expanded.value = !expanded.value;
  // Clicking always wins over the hover preview, on both touch and desktop.
  showPopover.value = false;
  clearPopoverHideTimer();
}

onBeforeUnmount(() => {
  clearPopoverHideTimer();
});
</script>
